import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { CSSObject } from 'styled-components';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';
import { MenuStyled } from './menu.styled';
import Portal from '../Portal';

interface MenuProps {
  placement?:
    | 'bottom-end'
    | 'overlap'
    | 'right-bottom'
    | 'left-bottom'
    | 'customize-position';
  children?: React.ReactNode | React.ReactNode[];
  anchorEl: HTMLElement;
  onClose: () => void;
  onClick?: () => void;
  open: boolean;
  sx?: CSSObject;
  style?: React.CSSProperties;
  id?:string;

  customizePosition?: ({
    top,
    bottom,
    left,
    right,
    width,
    height,
    modalStyle,
    modalWidth,
  }: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    width?: number;
    height?: number;
    modalWidth?: number;
    modalStyle: CSSStyleDeclaration;
  }) => React.CSSProperties;
}

const Menu = ({
  open,
  onClick,
  onClose = () => {},
  placement = 'bottom-end',
  anchorEl,
  children,
  customizePosition,
  style = {},
  ...rest
}: MenuProps) => {
  const [position, setPosition] = useState({});
  const modalRef = useRef<HTMLDivElement | null>(null);
  const desktopMode = isInDesktopMode();
  const compatibleWithTab = useDesktopCompatibility({
    feature: 'tab',
  });

  // TODO create hook -> useCoordinates
  const setMenuCoordinates = () => {
    if (anchorEl !== null && modalRef.current !== null) {
      const { top, bottom, left, right, width, height } =
        anchorEl.getBoundingClientRect();
      const { width: modalWidth } = modalRef.current.getBoundingClientRect();
      const modalStyle = getComputedStyle(modalRef.current);

      // set position on top of anchor element
      const topPosition = `calc(${
        desktopMode && compatibleWithTab ? 0 : top + height
      }px - ${modalStyle.borderWidth} + ${window.scrollY}px)`;

      if (placement === 'bottom-end') {
        setPosition({
          left: `calc(${left - modalWidth + width}px + ${
            modalStyle.borderWidth
          })`,
          top: topPosition,
        });
      } else if (placement === 'customize-position') {
        setPosition(
          customizePosition({
            top,
            left,
            right,
            width,
            height,
            bottom,
            modalStyle,
            modalWidth,
          }) || {
            right: 0,
            top: topPosition,
          },
        );
      } else if (placement === 'left-bottom') {
        setPosition({
          left: `calc(${right}px - ${modalStyle.borderWidth})`,
          top: topPosition,
        });
      } else if (placement === 'overlap') {
        setPosition({
          left,
          top: top + window.scrollY,
        });
      } else if (placement === 'right-bottom') {
        setPosition({
          left: `calc(${right}px - ${modalStyle.borderWidth})`,
          top: `calc(${bottom}px - ${modalStyle.height} + ${modalStyle.borderWidth} + ${window.scrollY}px)`,
        });
      }
    }
  };

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [open],
  );

  useLayoutEffect(() => setMenuCoordinates(), [anchorEl, open]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleOutsideClick = useCallback(
    (event: any) => {
      if (
        open &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose();
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);

    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [handleOutsideClick]);

  if (open) {
    return (
      <Portal>
        <MenuStyled
          onClick={onClick}
          ref={modalRef}
          style={{ ...position, ...style }}
          {...rest}
        >
          {children}
        </MenuStyled>
      </Portal>
    );
  }

  return null;
};

export default Menu;
